import { useEffect, useRef } from "react";
import { toast } from "@/hooks/use-toast";
import { hasReloadHold } from "@/lib/reload-guard";

const BUILD_ID = import.meta.env.VITE_BUILD_ID as string;
const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const MIN_HIDDEN_MS = 60 * 1000;
const AUTO_RELOAD_DELAY_MS = 8000;

/**
 * Routes where an automatic reload would throw away work in progress: the auth pages
 * (a member returns from their mail app holding a code), the verify pages (a reload
 * re-sends a code), and every card route (composer, delivery, sign) — the send flow must
 * never be pulled out from under the member. The hold counter (`useReloadHold`, taken by
 * every open Dialog) covers the same moments anywhere else.
 */
const NO_AUTO_RELOAD =
  /^\/(forgot|forgot-password|email-verify|verify|login|register|auth|card)(\/|$)/;

function safeToAutoReload(): boolean {
  if (hasReloadHold()) return false;
  if (NO_AUTO_RELOAD.test(window.location.pathname)) return false;
  // Belt and braces for overlays that aren't our Dialog, and a half-typed field.
  if (document.querySelector('[role="dialog"], [role="alertdialog"]')) return false;
  const active = document.activeElement;
  if (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement) return false;
  return true;
}

async function fetchBuildId(): Promise<string | null> {
  try {
    const res = await fetch("/version.json", { cache: "no-store" });
    if (!res.ok) return null;
    const json = (await res.json()) as { buildId?: string };
    return json.buildId ?? null;
  } catch {
    return null;
  }
}

export function useVersionCheck() {
  const notifiedRef = useRef(false);
  const staleRef = useRef(false);
  const hiddenAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (BUILD_ID === "dev") return;
    let reloadTimer: ReturnType<typeof setTimeout> | null = null;

    // Auto-reload only on a safe idle route, re-checked when the timer fires (the member
    // may have opened a dialog or moved into the composer in the meantime). Anywhere else
    // the toast's own tap is the only reload.
    function maybeAutoReload() {
      if (reloadTimer || !safeToAutoReload()) return;
      reloadTimer = setTimeout(() => {
        reloadTimer = null;
        if (safeToAutoReload()) window.location.reload();
      }, AUTO_RELOAD_DELAY_MS);
    }

    async function check() {
      if (staleRef.current) return maybeAutoReload();
      const remote = await fetchBuildId();
      if (remote && remote !== BUILD_ID) {
        staleRef.current = true;
        if (!notifiedRef.current) {
          notifiedRef.current = true;
          toast({
            variant: "info",
            title: "A new version of BirthdayAlarm is available",
            description: "Tap here to reload when you\u2019re ready.",
            onClick: () => window.location.reload(),
          });
        }
        maybeAutoReload();
      }
    }

    function onVisibilityChange() {
      if (document.hidden) {
        hiddenAtRef.current = Date.now();
      } else if (hiddenAtRef.current && Date.now() - hiddenAtRef.current >= MIN_HIDDEN_MS) {
        hiddenAtRef.current = null;
        void check();
      }
    }

    document.addEventListener("visibilitychange", onVisibilityChange);
    const interval = setInterval(() => void check(), CHECK_INTERVAL_MS);

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      clearInterval(interval);
      if (reloadTimer) clearTimeout(reloadTimer);
    };
  }, []);
}
